import { UTCDate } from '@date-fns/utc';
import { TPPAdapters } from '@seapoint/server/enums';
import { differenceInDays } from 'date-fns';
import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import {
  TellMoneyConsent,
  TellMoneyConsentScope,
  TellMoneyConsentStatus,
  TellMoneyNextGenPSD2AccountPermission,
  TellMoneyNextGenPSD2Permissions,
  TellMoneyOBIEPermission,
} from '../../../../server/src/adapters/tpp/tell-money/types/consent.types';
import { useTppApi } from '../../api/use-tpp-api';
import { useData } from '../../contexts/data-context/use-data';
import { Avatar } from '../../design-system/avatar/avatar/avatar';
import { Button } from '../../design-system/button/button';
import { ErrorSection } from '../../design-system/error-section/error-section';
import { Inline } from '../../design-system/inline/inline';
import { LoadingSection } from '../../design-system/loading/loading';
import {
  Modal,
  ModalCloseButton,
  ModalContent,
  ModalDescription,
  ModalFooter,
  ModalHeader,
  ModalTitle,
} from '../../design-system/modal/modal';
import { Stack } from '../../design-system/stack/stack';
import { AccountView } from '../../types/entity-types';
import { cn } from '../../utils/cn';
import { getBankAccountName } from '../../utils/get-bank-account-name';

// Define our working consent type based on TellMoneyConsent
type Consent = TellMoneyConsent;

// Badge component for permissions
const Badge: React.FC<{
  children: React.ReactNode;
  className?: string;
  onClick?: () => void;
}> = ({ children, className, onClick }) => {
  return (
    <span
      className={cn(
        'rounded-full bg-blue-50 px-2.5 py-1 text-xs font-medium text-blue-700',
        onClick && 'transition-colors hover:bg-blue-100',
        className,
      )}
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
    >
      {children}
    </span>
  );
};

const ConsentSettings: React.FC = () => {
  const [activeConsents, setActiveConsents] = useState<Consent[]>([]);
  const [expiredConsents, setExpiredConsents] = useState<Consent[]>([]);
  const [revokedConsents, setRevokedConsents] = useState<Consent[]>([]);
  const { getUserConsents, error, isLoading } = useTppApi();
  const { data } = useData();

  const fetchConsents = async () => {
    const consents = await getUserConsents();

    // Filter consents by status
    const active: Consent[] = consents.filter(
      (consent: Consent) =>
        consent.ConsentStatus === TellMoneyConsentStatus.AUTHORISED ||
        consent.ConsentStatus === TellMoneyConsentStatus.CONSUMED,
    );

    const expired: Consent[] = consents.filter(
      (consent: Consent) => consent.ConsentStatus === TellMoneyConsentStatus.EXPIRED,
    );

    const revoked: Consent[] = consents.filter(
      (consent: Consent) => consent.ConsentStatus === TellMoneyConsentStatus.REVOKED,
    );

    setActiveConsents(active);
    setExpiredConsents(expired);
    setRevokedConsents(revoked);
  };

  useEffect(() => {
    fetchConsents();
  }, []);

  if (isLoading) {
    return (
      <div className="fixed inset-0 flex items-center justify-center">
        <LoadingSection text="Loading consent settings..." />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <ErrorSection />
        <p className="mt-4 text-center text-gray-600">{error}</p>
      </div>
    );
  }

  return (
    <>
      <div className="container py-6 pl-6">
        <Stack space={8}>
          <div>
            <h2 className="mb-4 text-lg font-semibold">Active Consents</h2>
            <div className="space-y-6">
              {activeConsents.length > 0 ? (
                activeConsents.map((consent) => (
                  <ConsentCard key={consent.ConsentId} consent={consent} accounts={data?.accounts || []} />
                ))
              ) : (
                <p className="text-sm text-gray-500">No active consents found.</p>
              )}
            </div>
          </div>

          <div>
            <h2 className="mb-4 text-lg font-semibold">Expired Consents</h2>
            <div className="space-y-6">
              {expiredConsents.length > 0 ? (
                expiredConsents.map((consent) => (
                  <ConsentCard key={consent.ConsentId} consent={consent} accounts={data?.accounts || []} isExpired />
                ))
              ) : (
                <p className="text-sm text-gray-500">No expired consents found.</p>
              )}
            </div>
          </div>

          <div>
            <h2 className="mb-4 text-lg font-semibold">Revoked Consents</h2>
            <div className="space-y-6">
              {revokedConsents.length > 0 ? (
                revokedConsents.map((consent) => (
                  <ConsentCard key={consent.ConsentId} consent={consent} accounts={data?.accounts || []} isRevoked />
                ))
              ) : (
                <p className="text-sm text-gray-500">No revoked consents found.</p>
              )}
            </div>
          </div>
        </Stack>
      </div>
    </>
  );
};

interface ConsentCardProps {
  consent: Consent;
  accounts: AccountView[];
  isExpired?: boolean;
  isRevoked?: boolean;
}

// Define account types with and without permissions
interface AccountWithPermissions {
  id: string;
  name: string;
  currency: string;
  company: string;
  permissions: string[];
}

interface AccountWithoutPermissions {
  id: string;
  name: string;
  currency: string;
  company: string;
}

type AccountInfo = AccountWithPermissions | AccountWithoutPermissions;

const ConsentCard: React.FC<ConsentCardProps> = ({ consent, accounts, isExpired, isRevoked }) => {
  const [showAllPermissions, setShowAllPermissions] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const { deleteConsent } = useTppApi();

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'N/A';
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    }).format(new UTCDate(dateString));
  };

  const getDaysRemaining = (dateString: string | null) => {
    if (!dateString) return 0;
    const expiryDate = new UTCDate(dateString);
    const now = new UTCDate();
    return Math.max(0, differenceInDays(expiryDate, now));
  };

  const daysRemaining = consent.ConsentExpiry ? getDaysRemaining(consent.ConsentExpiry) : 0;
  const isEUAdapter = consent.GatewayDomain === 'seapoint-eu';

  // Format permissions for display
  const formatPermissions = () => {
    if (!consent.Permissions) return [];

    // Handle OBIE permissions (UK)
    if (Array.isArray(consent.Permissions)) {
      return (consent.Permissions as TellMoneyOBIEPermission[]).map((permission) => {
        // Convert from camelCase to readable format
        return permission
          .replace(/([A-Z])/g, ' $1')
          .replace(/^./, (str) => str.toUpperCase())
          .trim();
      });
    }

    // For EU adapters, we'll handle permissions differently
    // Just return a simple list of permission types for the header section
    const permissions: string[] = [];
    const psd2Permissions = consent.Permissions as TellMoneyNextGenPSD2Permissions;

    if (psd2Permissions.accounts && psd2Permissions.accounts.length > 0) {
      permissions.push('Account Info');
    }
    if (psd2Permissions.balances && psd2Permissions.balances.length > 0) {
      permissions.push('Balances');
    }
    if (psd2Permissions.transactions && psd2Permissions.transactions.length > 0) {
      permissions.push('Transactions');
    }

    return permissions;
  };

  // Get consented accounts information with their permissions for EU adapters
  const getConsentedAccounts = () => {
    if (!consent.ConsentedAccounts || consent.ConsentedAccounts.length === 0) {
      return [] as AccountInfo[];
    }

    // For EU adapters, we need to map permissions to accounts
    if (isEUAdapter && !Array.isArray(consent.Permissions)) {
      const psd2Permissions = consent.Permissions as TellMoneyNextGenPSD2Permissions;
      const accountPermissionsMap = new Map<string, string[]>();

      // Collect all permissions by account ID
      const processPermissions = (
        permissions: TellMoneyNextGenPSD2AccountPermission[] | undefined,
        permType: string,
      ) => {
        if (!permissions) return;
        permissions.forEach((perm) => {
          const accountId = consent.ConsentedAccounts?.find((id) => {
            const account = accounts.find((acc) => acc.id === id);
            return account?.accountInformation?.iban === perm.AccountId;
          });

          if (accountId) {
            if (!accountPermissionsMap.has(accountId)) {
              accountPermissionsMap.set(accountId, []);
            }
            accountPermissionsMap.get(accountId)?.push(permType);
          }
        });
      };

      processPermissions(psd2Permissions.accounts, 'Account');
      processPermissions(psd2Permissions.balances, 'Balance');
      processPermissions(psd2Permissions.transactions, 'Transaction');

      // Map account IDs to account names with permissions
      return consent.ConsentedAccounts.map((accountId) => {
        const account = accounts.find((acc) => acc.id === accountId);
        const permissions = accountPermissionsMap.get(accountId) || [];

        const accountName = getBankAccountName({
          displayName: account?.displayName,
          institutionName: account?.institution?.name,
        });

        return account
          ? {
              id: accountId,
              name: accountName,
              currency: account.currency,
              company: account.company?.legalEntity?.name || 'Unknown',
              permissions,
            }
          : {
              id: accountId,
              name: 'Unknown Account',
              currency: '',
              company: 'Unknown',
              permissions,
            };
      }) as AccountWithPermissions[];
    }

    // For UK adapters, just map account IDs to account names
    return consent.ConsentedAccounts.map((accountId) => {
      const account = accounts.find((acc) => acc.id === accountId);
      return account
        ? {
            id: accountId,
            name: account.displayName ?? account.institution?.name,
            currency: account.currency,
            company: account.company?.legalEntity?.name || 'Unknown',
          }
        : {
            id: accountId,
            name: 'Unknown Account',
            currency: '',
            company: 'Unknown',
          };
    }) as AccountWithoutPermissions[];
  };

  const formattedPermissions = formatPermissions();
  const consentedAccounts = getConsentedAccounts();

  // Type guard to check if an account has permissions
  const hasPermissions = (account: AccountInfo): account is AccountWithPermissions => {
    return 'permissions' in account;
  };

  // Handle consent deletion
  const handleDeleteConsent = async () => {
    setIsDeleting(true);

    // Determine the adapter type based on the gateway domain
    const adapterType = consent.GatewayDomain === 'seapoint-eu' ? TPPAdapters.TELL_MONEY_EU : TPPAdapters.TELL_MONEY_UK;

    const success = await deleteConsent(consent.ConsentId, adapterType);

    if (success) {
      toast.success('Consent revoked successfully');
      // Refresh the consents list
      window.location.reload();
    } else {
      toast.error('Failed to revoke the consent. Please try again.');
    }

    setIsDeleting(false);
    setShowDeleteModal(false);
  };

  return (
    <>
      <div
        className={cn(
          'overflow-hidden rounded-lg border bg-white shadow-sm',
          isExpired ? 'border-amber-200' : isRevoked ? 'border-red-200' : 'border-gray-200',
        )}
      >
        <div
          className={cn(
            'border-b p-4',
            isExpired ? 'border-amber-200 bg-amber-50' : isRevoked ? 'border-red-200 bg-red-50' : 'border-gray-200',
          )}
        >
          <Inline space={3} align="center" justify="between">
            <Inline space={3} align="center">
              <Avatar name={consent.TppName} size="sm" />
              <Stack space={1}>
                <h3 className="text-sm font-medium text-gray-900">{consent.TppName}</h3>
                <p className="text-xs text-gray-500">{consent.AppName}</p>
                {isExpired && <span className="text-xs font-medium text-amber-700">Expired</span>}
                {isRevoked && <span className="text-xs font-medium text-red-700">Revoked</span>}
              </Stack>
            </Inline>
            <div className="max-w-[50%] flex-shrink-0 overflow-hidden">
              {formattedPermissions.length > 0 &&
                (showAllPermissions || formattedPermissions.length <= 3 ? (
                  <Inline wrap="wrap" space={2}>
                    {formattedPermissions.map((permission, index) => (
                      <Badge key={index}>{permission}</Badge>
                    ))}
                    {showAllPermissions && formattedPermissions.length > 3 && (
                      <Badge
                        className="cursor-pointer bg-gray-50 text-gray-700"
                        onClick={() => setShowAllPermissions(false)}
                      >
                        Show less
                      </Badge>
                    )}
                  </Inline>
                ) : (
                  <Inline wrap="wrap" space={2}>
                    {formattedPermissions.slice(0, 2).map((permission, index) => (
                      <Badge key={index}>{permission}</Badge>
                    ))}
                    <Badge
                      className="cursor-pointer bg-gray-50 text-gray-700"
                      onClick={() => setShowAllPermissions(true)}
                    >
                      +{formattedPermissions.length - 2} more
                    </Badge>
                  </Inline>
                ))}
            </div>
          </Inline>
        </div>

        <div className="p-4">
          <Stack space={4}>
            {/* Consented Accounts Section */}
            <div>
              <h4 className="mb-2 text-xs font-medium text-gray-700">Consented Accounts</h4>
              {consentedAccounts.length > 0 ? (
                <div className="max-h-40 overflow-y-auto rounded-md bg-gray-50 p-2">
                  <Stack space={2}>
                    {consentedAccounts.map((account, index) => (
                      <div key={index} className="rounded-md bg-white p-2 shadow-sm">
                        <Inline align="center" space={2} wrap="nowrap" className="w-full">
                          <span className="h-2 w-2 flex-shrink-0 rounded-full bg-green-500"></span>
                          <span className="truncate text-xs font-medium text-gray-700">{account.name}</span>
                          {account.currency && (
                            <span className="flex-shrink-0 text-xs text-gray-500">({account.currency})</span>
                          )}
                        </Inline>

                        {isEUAdapter && hasPermissions(account) && account.permissions.length > 0 && (
                          <Inline space={2} className="ml-4 mt-2">
                            {account.permissions.map((perm: string, i: number) => (
                              <span key={i} className="rounded-md bg-gray-100 px-2 py-0.5 text-xs text-gray-600">
                                {perm}
                              </span>
                            ))}
                          </Inline>
                        )}
                      </div>
                    ))}
                  </Stack>
                </div>
              ) : (
                <span className="text-xs text-gray-500">No accounts consented</span>
              )}
              {!isEUAdapter && consentedAccounts.length > 0 && (
                <p className="mt-2 text-xs text-gray-500">All permissions apply to all accounts</p>
              )}
            </div>

            <div className="rounded-md bg-gray-50 p-3">
              <Inline space={6} justify="between">
                <Stack space={1}>
                  <h4 className="text-xs font-medium text-gray-700">Expiry</h4>
                  <p className="text-xs text-gray-600">
                    {formatDate(consent.ConsentExpiry)}
                    {daysRemaining > 0 && (
                      <span className="ml-2 text-xs text-gray-500">({daysRemaining} days remaining)</span>
                    )}
                  </p>
                </Stack>

                <Stack space={1}>
                  <h4 className="text-xs font-medium text-gray-700">Type</h4>
                  <p className="text-xs text-gray-600">
                    {consent.Scope === TellMoneyConsentScope.ACCOUNTS
                      ? 'Account Access'
                      : consent.Scope === TellMoneyConsentScope.PAYMENTS
                        ? 'Payment'
                        : consent.Scope === TellMoneyConsentScope.FUNDS_CONFIRMATIONS
                          ? 'Funds'
                          : consent.Scope}
                  </p>
                </Stack>
              </Inline>
            </div>

            <Inline justify="between" align="center" className="w-full">
              <Button
                variant="link"
                className="justify-start p-0 text-xs"
                onClick={() => window.open(consent.TppWebsite, '_blank', 'noopener,noreferrer')}
              >
                Visit {consent.TppName} website
              </Button>

              {!isExpired && !isRevoked && (
                <Button variant="destructive" onClick={() => setShowDeleteModal(true)}>
                  Revoke Consent
                </Button>
              )}
            </Inline>
          </Stack>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      <Modal open={showDeleteModal} onOpenChange={setShowDeleteModal}>
        <ModalContent className="h-auto max-w-md">
          <ModalCloseButton />
          <ModalHeader>
            <ModalTitle>Revoke Consent</ModalTitle>
            <ModalDescription>
              Are you sure you want to revoke consent for {consent.TppName}? This action cannot be undone.
            </ModalDescription>
          </ModalHeader>
          <ModalFooter>
            <Button variant="outline" onClick={() => setShowDeleteModal(false)}>
              Cancel
            </Button>
            <Button variant="destructive" isLoading={isDeleting} onClick={handleDeleteConsent}>
              Revoke Consent
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

export default ConsentSettings;
